import {
  useApiKeyStatus,
  useCurrentAccount,
  useRegenerateApiKey,
} from '@gripp/shared-logic';
import {
  Button,
  Colors,
  CustomAlert,
  Spacing,
  typography,
  useSlideLayout,
} from '@gripp/shared-ui';
import { useCallback, useEffect, useState } from 'react';
import Toast from 'react-native-toast-message';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';

export const SettingsApiKey = () => {
  const { setColumnTitle } = useSlideLayout();
  const { t } = useTranslation();
  const { account } = useCurrentAccount();
  const { apiKey, isLoading } = useApiKeyStatus();
  const { mutateAsync: regenerate, isPending } = useRegenerateApiKey();

  const [plaintextKey, setPlaintextKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [workspaceIdCopied, setWorkspaceIdCopied] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);

  useEffect(() => {
    setColumnTitle(t('preferences.settings.apiKey'));
  }, [setColumnTitle, t]);

  const handleGenerate = useCallback(async () => {
    const result = await regenerate();
    setPlaintextKey(result.plaintextKey);
    setCopied(false);
  }, [regenerate]);

  const handleRegenerate = useCallback(() => {
    if (apiKey) {
      setConfirmVisible(true);
    } else {
      handleGenerate();
    }
  }, [apiKey, handleGenerate]);

  const handleConfirmRegenerate = useCallback(async () => {
    setConfirmVisible(false);
    await handleGenerate();
  }, [handleGenerate]);

  const handleCopy = useCallback(async () => {
    if (plaintextKey) {
      try {
        await navigator.clipboard.writeText(plaintextKey);
        setCopied(true);
      } catch {
        Toast.show({ type: 'error', text1: t('apiKey.copyError') });
      }
    }
  }, [plaintextKey, t]);

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={Colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <ScrollView contentContainerStyle={styles.contentContainer}>
        <Text style={typography.title.smallEmphasised}>
          {t('preferences.settings.apiKey')}
        </Text>
        <Text style={[typography.body.small, styles.description]}>
          {t('apiKey.description')}
        </Text>

        {account?.workspace?.id && (
          <View style={styles.existingKey}>
            <View style={styles.keyInfoRow}>
              <Text style={typography.label.medium}>
                {t('apiKey.workspaceId')}
              </Text>
              <Pressable
                onPress={async () => {
                  try {
                    await navigator.clipboard.writeText(account.workspace!.id!);
                    setWorkspaceIdCopied(true);
                    setTimeout(() => setWorkspaceIdCopied(false), 2000);
                  } catch {
                    Toast.show({ type: 'error', text1: t('apiKey.copyWorkspaceIdError') });
                  }
                }}
              >
                <Text style={[typography.body.small, styles.copyableText]}>
                  {workspaceIdCopied ? t('apiKey.copied') : account.workspace.id}
                </Text>
              </Pressable>
            </View>
          </View>
        )}

        {plaintextKey && (
          <View style={styles.createdAlert}>
            <Text style={[typography.label.mediumEmphasised, styles.warningText]}>
              {t('apiKey.copyWarning')}
            </Text>
            <View style={styles.keyDisplay}>
              <Text style={[typography.body.small, styles.keyText]} selectable>
                {plaintextKey}
              </Text>
            </View>
            <Button
              onPress={handleCopy}
              state={copied ? 'default' : 'active'}
              size="large"
              style={styles.actionButton}
            >
              {copied ? t('apiKey.copied') : t('apiKey.copy')}
            </Button>
          </View>
        )}

        {!plaintextKey && apiKey && (
          <View style={styles.existingKey}>
            <View style={styles.keyInfoRow}>
              <Text style={typography.label.medium}>{t('apiKey.currentKey')}</Text>
              <Text style={typography.body.small}>{apiKey.keyPrefix}...</Text>
            </View>
            <View style={styles.keyInfoRow}>
              <Text style={typography.label.medium}>{t('apiKey.created')}</Text>
              <Text style={typography.body.small}>
                {new Date(apiKey.createdAt).toLocaleDateString()}
              </Text>
            </View>
            {apiKey.lastUsedAt && (
              <View style={styles.keyInfoRow}>
                <Text style={typography.label.medium}>{t('apiKey.lastUsed')}</Text>
                <Text style={typography.body.small}>
                  {new Date(apiKey.lastUsedAt).toLocaleDateString()}
                </Text>
              </View>
            )}
          </View>
        )}
      </ScrollView>

      <View style={styles.contentBottomBar}>
        <Button
          onPress={handleRegenerate}
          state={isPending ? 'default' : 'active'}
          size="large"
          style={styles.actionButton}
        >
          {isPending ? (
            <ActivityIndicator size="small" color={Colors.white} />
          ) : apiKey && !plaintextKey ? (
            t('apiKey.regenerate')
          ) : (
            t('apiKey.generate')
          )}
        </Button>
      </View>

      <CustomAlert
        visible={confirmVisible}
        hideAlert={() => setConfirmVisible(false)}
        action={handleConfirmRegenerate}
        title={t('apiKey.regenerateConfirm')}
        primaryButtonLabel={t('apiKey.regenerateConfirmYes')}
        secondaryButtonLabel={t('topNav.customAlert.cancel')}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  contentContainer: {
    paddingVertical: Spacing.basePadding.paddingVertical,
    paddingHorizontal: Spacing.basePadding.paddingHorizontal,
    gap: 16,
  },
  description: {
    color: Colors.secondaryText,
  },
  createdAlert: {
    backgroundColor: Colors.primaryGrayBackground,
    padding: 16,
    borderRadius: 8,
    gap: 12,
  },
  warningText: {
    color: Colors.orange,
  },
  keyDisplay: {
    backgroundColor: Colors.white,
    padding: 12,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: Colors.primaryGrayBorder,
  },
  keyText: {
    fontFamily: 'monospace',
  },
  copyableText: {
    fontFamily: 'monospace',
    color: Colors.primary,
  },
  existingKey: {
    backgroundColor: Colors.primaryGrayBackground,
    padding: 16,
    borderRadius: 8,
    gap: 8,
  },
  keyInfoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  contentBottomBar: {
    padding: Spacing.baseMargin.base,
    backgroundColor: Colors.white,
  },
  actionButton: {
    borderRadius: 2,
    width: '100%',
    height: 48,
  },
});
